import React from 'react';
import { IntlShape } from 'react-intl';
import { getTranslatedText } from '@nile/components';

/**
 * Properties in uiDataProp that should be treated as localization keys
 * Add new localizable properties here as they are introduced
 */
export const LOCALIZABLE_UI_PROPS = [
  'label',
  'placeholder',
  'error',
  'title',
  'description',
  'ariaLabel',
  'ariaDescription',
  'helpText'
] as const;

/**
 * Generic utility to translate localized keys in uiDataProp
 * @param intl - React Intl instance
 * @param uiDataProp - The uiDataProp object that may contain localization keys
 * @returns Translated uiDataProp object
 */
export const translateUiDataProp = (
  intl: IntlShape,
  uiDataProp: Record<string, any> | null | undefined
): Record<string, any> => {
  if (!uiDataProp) return {};

  const translatedProps = { ...uiDataProp };

  // Translate all localizable properties that exist and are strings
  LOCALIZABLE_UI_PROPS.forEach((propKey) => {
    if (uiDataProp[propKey] && typeof uiDataProp[propKey] === 'string') {
      translatedProps[propKey] = getTranslatedText(intl, uiDataProp[propKey]);
    }
  });

  return translatedProps;
};

/**
 * Utility to get translated value for a specific property
 * @param intl - React Intl instance
 * @param value - The value to translate (could be localization key or regular text)
 * @returns Translated text or original value if not a string
 */
export const getTranslatedProp = (intl: IntlShape, value: any): any => {
  if (typeof value === 'string') {
    return getTranslatedText(intl, value, {
      br: <br />,
      ol: (chunks) => <ol>{chunks}</ol>,
      ul: (chunks) => <ul>{chunks}</ul>,
      li: (chunks) => <li>{chunks}</li>,
      strong: (chunks) => <strong>{chunks}</strong>,
      em: (chunks) => <em>{chunks}</em>,
      div: (chunks) => <div>{chunks}</div>,
      b: (chunks) => <b>{chunks}</b>
    });
  }
  return value;
};
